import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { LiveBadge } from "@/components/pakistan/LiveBadge";
import { PakistanNavigator, PakistanBreadcrumbSchema } from "@/components/pakistan/PakistanNavigator";
import { FaqSection } from "@/components/FaqSection";
import {
  PAKISTAN_CITIES,
  INDIA_CITIES,
  computeCityPrices,
  formatMoney,
  GRAMS_PER_TOLA,
} from "@/lib/gold-data";
import { goldRatesQueryOptions, useGoldRates } from "@/lib/nonblocking-rates";
import { SITE_URL } from "@/lib/seo";

/**
 * /pakistan/vs/india — side-by-side live comparison of Pakistan and India
 * gold rates. Uses same live price data; converts both to USD/gram for a
 * fair apples-to-apples comparison, plus native-currency per-tola and
 * per-10g for readability.
 */
export const Route = createFileRoute("/pakistan/vs/india")({
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(goldRatesQueryOptions);
  },
  head: () => {
    const url = `${SITE_URL}/pakistan/vs/india`;
    return {
      meta: [
        { title: "Gold Rate Pakistan vs India — Live Comparison Today" },
        {
          name: "description",
          content:
            "Live side-by-side comparison of 24K, 22K and 21K gold rates in Pakistan vs India. Karachi vs Delhi vs Mumbai — per tola, per 10 grams and per gram in USD.",
        },
        { property: "og:title", content: "Gold Rate Pakistan vs India — Live Comparison" },
        {
          property: "og:description",
          content: "Karachi vs Delhi vs Mumbai — live 24K/22K gold rate comparison.",
        },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: PakistanVsIndia,
});

function PakistanVsIndia() {
  const { data } = useGoldRates();

  const pk = PAKISTAN_CITIES.slice(0, 5).map((c) => ({
    city: c,
    prices: computeCityPrices({
      spotUsdPerOz: data.spotUsdPerOz,
      fxRate: data.pkrPerUsd,
      country: "pakistan",
      cityPremium: c.premium,
    }),
  }));
  const inC = INDIA_CITIES.slice(0, 5).map((c) => ({
    city: c,
    prices: computeCityPrices({
      spotUsdPerOz: data.spotUsdPerOz,
      fxRate: data.inrPerUsd,
      country: "india",
      cityPremium: c.premium,
    }),
  }));

  const usdPerGramPk = pk[0].prices.perGram24 / data.pkrPerUsd;
  const usdPerGramIn = inC[0].prices.perGram24 / data.inrPerUsd;
  const gapPct = (((usdPerGramIn - usdPerGramPk) / usdPerGramPk) * 100).toFixed(1);

  return (
    <Layout>
      <section className="mx-auto max-w-5xl px-4 py-8">
        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/" className="hover:underline">Home</Link> ›{" "}
          <Link to="/pakistan" className="hover:underline">Pakistan</Link> › vs India
        </nav>
        <h1 className="text-3xl md:text-4xl font-bold">Gold Rate: Pakistan vs India</h1>
        <p className="mt-2 text-muted-foreground">
          Live 24K gold rate side-by-side across the top 5 cities in each country, updated every
          10 minutes. Both markets derive from the same international spot price (XAU/USD) but
          differ due to duty, GST and local currency.
        </p>
        <div className="mt-3">
          <LiveBadge label="Live" />
        </div>

        <div className="mt-6 rounded-xl border border-primary/30 bg-primary/5 p-4 text-sm">
          <p>
            <strong>Apples-to-apples (USD / gram, 24K):</strong>{" "}
            Pakistan (Karachi) <strong>${usdPerGramPk.toFixed(2)}</strong> ·{" "}
            India (Delhi) <strong>${usdPerGramIn.toFixed(2)}</strong> · India premium over Pakistan:{" "}
            <strong>{gapPct}%</strong>. India ka gold Pakistan se {Number(gapPct) > 0 ? "mehnga" : "sasta"} hai
            mainly 15% customs duty + 3% GST ki wajah se.
          </p>
        </div>

        <div className="mt-6 grid gap-6 md:grid-cols-2">
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="bg-green-500/10 px-4 py-2 text-sm font-semibold">🇵🇰 Pakistan</div>
            <table className="w-full text-sm">
              <thead className="bg-muted/60 text-left">
                <tr>
                  <th className="px-3 py-2">City</th>
                  <th className="px-3 py-2 text-right">24K / tola</th>
                  <th className="px-3 py-2 text-right">22K / tola</th>
                </tr>
              </thead>
              <tbody>
                {pk.map(({ city, prices }) => (
                  <tr key={city.slug} className="border-t border-border">
                    <th scope="row" className="px-3 py-2 text-left font-medium">
                      <Link to="/$country/$city" params={{ country: "pakistan", city: city.slug }} className="hover:text-primary hover:underline">
                        {city.name}
                      </Link>
                    </th>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">{formatMoney(prices.perTola24, "PKR")}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatMoney(prices.perTola22, "PKR")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="bg-orange-500/10 px-4 py-2 text-sm font-semibold">🇮🇳 India</div>
            <table className="w-full text-sm">
              <thead className="bg-muted/60 text-left">
                <tr>
                  <th className="px-3 py-2">City</th>
                  <th className="px-3 py-2 text-right">24K / 10g</th>
                  <th className="px-3 py-2 text-right">22K / 10g</th>
                </tr>
              </thead>
              <tbody>
                {inC.map(({ city, prices }) => (
                  <tr key={city.slug} className="border-t border-border">
                    <th scope="row" className="px-3 py-2 text-left font-medium">
                      <Link to="/$country/$city" params={{ country: "india", city: city.slug }} className="hover:text-primary hover:underline">
                        {city.name}
                      </Link>
                    </th>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">{formatMoney(prices.per10g24, "INR")}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatMoney(prices.per10g22, "INR")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <article className="prose prose-sm max-w-none mt-8 text-foreground/90">
          <h2 className="text-xl font-bold">Why does India's gold cost more than Pakistan's?</h2>
          <p>
            International spot price ek hi hai — dono countries ka rate wahin se start hota hai.
            Pakistan mein direct: spot × USD/PKR + Rs 4,000/tola local premium. India mein: spot ×
            USD/INR + Rs 11,000/10g flat premium <strong>+ 10% duty multiplier</strong> (approx
            15% customs + 3% GST combined). Isliye India ka retail rate structurally Pakistan se
            higher rehta hai jab dono ko USD/gram mein compare karein.
          </p>
          <p>
            Dono countries mein tola/10g conversion different hai — Pakistan tola-based (11.66g),
            India 10g-based. Comparison fair banane ke liye humne dono ko US$/gram mein normalize
            kiya hai upar.
          </p>
        </article>

        <FaqSection
          faqs={[
            {
              q: "Pakistan aur India mein sona kahan sasta hai?",
              a: `Live USD/gram basis par Pakistan Karachi mein 24K $${usdPerGramPk.toFixed(2)}/g hai, India Delhi mein $${usdPerGramIn.toFixed(2)}/g — India ${gapPct}% mehnga hai due to customs duty + GST.`,
            },
            {
              q: "1 tola aur 10 gram mein kya farak hai?",
              a: `1 tola = ${GRAMS_PER_TOLA}g. Pakistan tola use karta hai, India 10g. Comparison ke liye per-gram rate use karein.`,
            },
            {
              q: "Kya dono ka gold same purity ka hota hai?",
              a: "Haan — 24K/22K/21K standard international hai. Dono countries mein BIS/HUID (India) aur PSQCA (Pakistan) hallmarking systems exist karte hain quality assurance ke liye.",
            },
            {
              q: "Cross-border trade legal hai?",
              a: "Personal use ke liye limits hain (India: Rs 1 lakh male / Rs 2 lakh female, duty-free from Pakistan travel). Commercial import mein full duty + GST apply hoti hai. Yeh page sirf informational hai — cross-border transaction se pehle current customs regulations check karein.",
            },
          ]}
          title="Pakistan vs India gold — FAQs"
        />

        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          <Link to="/pakistan" className="rounded-lg border border-border p-4 hover:border-primary">
            <div className="font-semibold">🇵🇰 Pakistan gold hub →</div>
            <div className="text-xs text-muted-foreground mt-1">All 21 cities, karats, units</div>
          </Link>
          <Link to="/india" className="rounded-lg border border-border p-4 hover:border-primary">
            <div className="font-semibold">🇮🇳 India gold hub →</div>
            <div className="text-xs text-muted-foreground mt-1">All 20 cities, karats, units</div>
          </Link>
        </div>
        <PakistanBreadcrumbSchema
          items={[
            { name: "Pakistan", href: "https://drhint.com/pakistan" },
            { name: "vs India", href: "https://drhint.com/pakistan/vs/india" },
          ]}
        />
        <PakistanNavigator exclude="/pakistan/vs/india" />
      </section>
    </Layout>
  );
}